import { useReducedMotion } from 'motion/react'
import { useShopI18n } from '../../i18n'
import { buildCustomerAreaNavItems, CustomerAreaLayoutShell } from './customer-area'
import { OrdersErrorBanner, OrdersManagementPanel } from './orders'
import { useCustomerOrdersListController } from './orders/useCustomerOrdersListController'
import type { CustomerOrder } from '../customerOrdersApi'

type CustomerOrdersListPageProps = {
  orders: CustomerOrder[]
  ordersLoading: boolean
  ordersError: string
  checkoutSyncActive: boolean
  onNavigate: (targetPath: string) => void
  onLogout: () => void
  onRetryOrders: () => void
}

export const CustomerOrdersListPage = ({
  orders,
  ordersLoading,
  ordersError,
  checkoutSyncActive,
  onNavigate,
  onLogout,
  onRetryOrders,
}: CustomerOrdersListPageProps) => {
  const { language, locale, t, toLocalizedPath } = useShopI18n()
  const reducedMotion = Boolean(useReducedMotion())

  const navItems = buildCustomerAreaNavItems({
    t,
    toLocalizedPath,
    language,
    ordersBadge: orders.length > 0 ? String(orders.length) : undefined,
  })

  const {
    searchValue,
    filtersOpen,
    sortBy,
    dateFilter,
    customDateFrom,
    customDateTo,
    statusFilters,
    priceMin,
    priceMax,
    pageSize,
    currentPage,
    dateFormatter,
    sortOptions,
    dateFilterOptions,
    pageSizeOptions,
    filteredOrders,
    totalPages,
    visiblePages,
    pagination,
    paginatedOrders,
    activeFilterChips,
    isLoadingSkeletonVisible,
    onSearchChange,
    onToggleFilters,
    onSortChange,
    onDateFilterChange,
    onCustomDateFromChange,
    onCustomDateToChange,
    onToggleStatusFilter,
    onPriceMinChange,
    onPriceMaxChange,
    onClearAllFilters,
    onOpenOrderDetail,
    onBuyAgain,
    onRequestSupport,
    onOpenCatalog,
    onSetPageSize,
    onSetCurrentPage,
  } = useCustomerOrdersListController({
    orders,
    ordersLoading,
    language,
    locale,
    t,
    toLocalizedPath,
    onNavigate,
  })

  const isLoading = isLoadingSkeletonVisible

  return (
    <CustomerAreaLayoutShell
      eyebrow={t('common.customerArea')}
      title={t('customer.orders.title')}
      subtitle={t('customer.orders.subtitle')}
      meta={
        checkoutSyncActive ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-600">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-500" aria-hidden />
            {t('customer.orders.syncInProgress')}
          </span>
        ) : undefined
      }
      navItems={navItems}
      activeNavId="orders"
      onNavigate={onNavigate}
      actions={[
        {
          id: 'continue-shopping',
          label: t('customer.orders.continueShopping'),
          onClick: () => onNavigate(toLocalizedPath('/')),
          variant: 'primary',
        },
        {
          id: 'logout',
          label: t('common.logout'),
          onClick: onLogout,
          variant: 'danger',
        },
      ]}
    >
      <OrdersErrorBanner ordersError={ordersError} retryLabel={t('common.retry')} onRetryOrders={onRetryOrders} />

      <OrdersManagementPanel
        reducedMotion={reducedMotion}
        loading={isLoading}
        ordersError={ordersError}
        orders={orders}
        filteredOrders={filteredOrders}
        paginatedOrders={paginatedOrders}
        pagination={pagination}
        pageSize={pageSize}
        currentPage={currentPage}
        totalPages={totalPages}
        visiblePages={visiblePages}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        sortBy={sortBy}
        filtersOpen={filtersOpen}
        dateFilter={dateFilter}
        customDateFrom={customDateFrom}
        customDateTo={customDateTo}
        statusFilters={statusFilters}
        priceMin={priceMin}
        priceMax={priceMax}
        sortOptions={sortOptions}
        dateFilterOptions={dateFilterOptions}
        activeFilterChips={activeFilterChips}
        locale={locale}
        language={language}
        dateFormatter={dateFormatter}
        onSearchChange={onSearchChange}
        onToggleFilters={onToggleFilters}
        onSortChange={onSortChange}
        onDateFilterChange={onDateFilterChange}
        onCustomDateFromChange={onCustomDateFromChange}
        onCustomDateToChange={onCustomDateToChange}
        onToggleStatusFilter={onToggleStatusFilter}
        onPriceMinChange={onPriceMinChange}
        onPriceMaxChange={onPriceMaxChange}
        onClearAllFilters={onClearAllFilters}
        onOpenOrderDetail={onOpenOrderDetail}
        onBuyAgain={onBuyAgain}
        onRequestSupport={onRequestSupport}
        onOpenCatalog={onOpenCatalog}
        onSetPageSize={onSetPageSize}
        onSetCurrentPage={onSetCurrentPage}
        t={t}
      />
    </CustomerAreaLayoutShell>
  )
}
